<template>
    <div class="table-calendar-view">
        <!-- Tarih alanı yoksa empty state göster -->
        <div v-if="!hasDateFields" class="calendar-empty-state">
            <a-empty>
                <template #description>
                    <div class="empty-state-content">
                        <h3>{{ $t('tables.notAvailable') }}</h3>
                        <p>{{ $t('tables.needDateField') }}</p>
                        <a-button type="primary" @click="switchToTableView">{{ $t('tables.switchToTable') }}</a-button>
                    </div>
                </template>
            </a-empty>
        </div>

        <!-- Tarih alanı varsa normal takvim görünümünü göster -->
        <template v-else>
            <!-- Header Container -->
            <div class="calendar-header">
                <!-- Renk Göstergesi -->
                <div class="color-legend-wrapper">
                    <div class="color-legend">
                        <div v-for="item in colorLegend" :key="item.value" class="legend-item">
                            <div class="color-box" :style="{ backgroundColor: item.color }"></div>
                            <span>{{ item.value }}</span>
                        </div>
                    </div>
                </div>

                <!-- Takvim Ayarları Butonu -->
                <SupsisButton type="primary" ghost @click="openCalendarSettings">
                    <i class="material-icons">settings</i>
                    <span>{{ $t('tables.calendarSettings') }}</span>
                </SupsisButton>
            </div>

            <!-- Takvim Ayarları Overlay -->
            <CalendarSettingsOverlay
                v-if="overlayVisible"
                :fields="table.fields"
                :calendarSettings="calendarSettings"
                @close="overlayVisible = false"
                @saveSettings="saveCalendarSettings"
            />
            <FullCalendar ref="fullCalendar" :options="calendarOptions" />
        </template>
    </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import CalendarSettingsOverlay from "./CalendarSettingsOverlay.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import moment from "moment";
import _ from "lodash";
import { mapActions, mapState } from "vuex";

export default {
    name: "TableCalendarView",
    components: {
        FullCalendar,
        CalendarSettingsOverlay,
        SupsisButton,
    },
    props: {
        records: {
            type: Array,
            default: () => [],
        },
        table: {
            type: Object,
            required: true,
        },
        selectedRecords: {
            type: Array,
            default: () => [],
        },
        isSelectionModeActive: {
            type: Boolean,
            default: false,
        },
        // Renk koşullarını tablo gibi yansıtmak isterseniz
        getRecordColor: {
            type: Function,
            required: true,
        },
        groupValue: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            overlayVisible: false,
            calendarSettings: {
                calendarFieldStart: null,
                calendarFieldEnd: null,
                cardFields: [],
                colorByField: null,
            },
            calendarOptions: {
                plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
                initialView: "dayGridMonth",
                headerToolbar: {
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,timeGridWeek,timeGridDay",
                },
                buttonText: {
                    today: this.$t('tables.today'),
                    month: this.$t('tables.month'),
                    week: this.$t('tables.week'),
                    day: this.$t('tables.day'),
                },
                editable: true,
                selectable: true,
                events: [],
                eventClick: this.onEventClick,
                dateClick: this.onDateClick,
            },
        };
    },
    computed: {
        colorLegend() {
            if (!this.calendarSettings.colorByField) return [];

            const field = this.getFieldObject(this.calendarSettings.colorByField);
            if (!field?.options?.dropdown?.options) return [];

            return field.options.dropdown.options.map((opt) => ({
                value: opt.value,
                color: opt.color || "rgb(210, 216, 243)",
            }));
        },
        hasDateFields() {
            return this.table.fields.some((f) => f.fieldType === "datetime" || f.fieldType === "date");
        },
        ...mapState({
            tables: (state) => state.table.tables,
        }),
    },
    created() {
        // LocalStorage'dan ayarları yükle veya varsayılan ayarları kullan
        this.loadCalendarSettings();
        this.calendarOptions.events = this.buildEventsFromRecords(this.records);
    },
    watch: {
        // Records değişince takvim etkinliklerini yeniliyoruz
        records: {
            deep: true,
            handler(newVal) {
                this.calendarOptions.events = this.buildEventsFromRecords(newVal);
            },
        },
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
        }),
        // Örnek: records içindeki tarih alanına göre FullCalendar event formatı oluşturma
        buildEventsFromRecords(records) {
            const startField = this.calendarSettings?.calendarFieldStart;
            const endField = this.calendarSettings?.calendarFieldEnd;
            const colorField = this.calendarSettings?.colorByField;

            if (!startField) return [];

            return records.map((record) => {
                // Kart başlığı için seçilen alanların değerlerini formatlayarak birleştir
                const titleParts = this.calendarSettings.cardFields.map((fieldId) => {
                    const field = this.getFieldObject(fieldId);
                    if (!field) return "";

                    const value = record.data[fieldId];
                    const formattedValue = this.getFormattedValue(field, value);
                    return `${field.name}: ${formattedValue}`;
                });

                // Renk belirleme
                let backgroundColor = this.getRecordColor(record);
                if (colorField) {
                    const field = this.getFieldObject(colorField);
                    const value = record.data[colorField];
                    if (field?.options?.dropdown?.options) {
                        const option = field.options.dropdown.options.find((opt) => opt.value === value);
                        if (option?.color) {
                            backgroundColor = option.color;
                        }
                    }
                }

                return {
                    id: record._id,
                    title: titleParts.join(" | "),
                    start: record.data[startField] || null,
                    end: record.data[endField] || null,
                    backgroundColor,
                    color: this.getContrastColor(backgroundColor),
                    borderColor: backgroundColor,
                };
            });
        },

        getFieldObject(fieldId) {
            return this.table.fields.find((f) => f._id === fieldId);
        },

        getFormattedValue(field, value) {
            if (!value && value !== 0) return "";

            switch (field.fieldType) {
                case "datetime":
                case "date":
                    if (field.options?.datetime) {
                        const timeCfg = field.options.datetime;
                        let formatStr = timeCfg.format || "YYYY-MM-DD";
                        if (timeCfg.includeTime && timeCfg.timeFormat) {
                            formatStr = `${formatStr} ${timeCfg.timeFormat}`;
                        }
                        return moment(value).format(formatStr);
                    }
                    return moment(value).format("YYYY-MM-DD");

                case "number":
                    if (field.options?.number?.decimalFormat) {
                        const floatVal = parseFloat(value);
                        return isNaN(floatVal) ? value : floatVal.toFixed(field.options.number.decimalFormat.length);
                    }
                    return value;

                case "currency":
                    if (field.options?.currency) {
                        const curSymbol = field.options.currency.symbol || "$";
                        const decimalPlace = field.options.currency.decimalFormat || "00";
                        const floatVal = parseFloat(value);
                        if (isNaN(floatVal)) return value;
                        return `${curSymbol}${floatVal.toFixed(decimalPlace.length)}`;
                    }
                    return value;

                case "dropdown":
                    if (Array.isArray(value)) {
                        return value.join(", ");
                    }
                    return value;

                case "contact":
                    return _.get(value, "fullname", "İsimsiz Kişi");

                case "user":
                    return _.get(value, "name", "İsimsiz Kullanıcı");

                case "task":
                    return _.get(value, "title", "İsimsiz Görev");

                case "linked-record":
                    if (!value) return "Bağlı Kayıt";
                    const labelField = field.options?.linkedRecord?.labelField;
                    const remoteTable = this.tables.find((t) => t._id === field.options.linkedRecord.table);
                    const remoteField = remoteTable.fields.find((f) => f._id === labelField);
                    if (remoteField.fieldType === "contact") {
                        const contact = _.get(value, `data.${labelField}`, undefined);
                        return contact ? contact.fullname : "İsimsiz Kişi";
                    } else if (remoteField.fieldType === "user") {
                        const user = _.get(value, `data.${labelField}`, undefined);
                        return user ? user.name : "İsimsiz Kullanıcı";
                    } else if (remoteField.fieldType === "task") {
                        const task = _.get(value, `data.${labelField}`, undefined);
                        return task ? task.title : "İsimsiz Görev";
                    } else {
                        return _.get(value, `data.${labelField}`, "Bağlı Kayıt");
                    }

                default:
                    return value;
            }
        },
        onEventClick(info) {
            const recordId = info.event.id;
            const record = this.records.find((r) => r._id === recordId);
            const label = info.event.title;
            // RecordViewer'ı aç
            this.addNewWindow({
                id: recordId,
                type: "table-record-viewer",
                title: `${this.table.name} - (${label})`,
                payload: {
                    tableId: this.table._id,
                    recordId: recordId,
                },
                multiple: true,
                autoClose: false,
            });
        },
        onDateClick(info) {
            // Yeni bir kayıt oluşturmak için info.date geçilebilir
            console.log("[Calendar] Date clicked =>", info.date);
        },
        openCalendarSettings() {
            this.overlayVisible = true;
        },
        saveCalendarSettings(newSettings) {
            this.calendarSettings = newSettings;
            // LocalStorage'a kaydet (tablo ID'sine göre)
            window.localStorage.setItem(`calendar_settings_${this.table._id}`, JSON.stringify(newSettings));
            // Etkinlikleri güncelle
            this.calendarOptions.events = this.buildEventsFromRecords(this.records);
        },
        loadCalendarSettings() {
            // LocalStorage'dan ayarları yükle
            const savedSettings = window.localStorage.getItem(`calendar_settings_${this.table._id}`);

            if (savedSettings) {
                this.calendarSettings = JSON.parse(savedSettings);
                return;
            }

            // Varsayılan ayarları belirle
            const defaultSettings = this.getDefaultSettings();
            this.calendarSettings = defaultSettings;

            // Varsayılan ayarları localStorage'a kaydet
            this.saveCalendarSettings(defaultSettings);
        },
        getDefaultSettings() {
            // Tarih tipindeki ilk alanı bul
            const defaultDateField = this.table.fields.find(
                (f) => f.fieldType === "datetime" || f.fieldType === "date"
            );

            // String tipindeki ilk alanı bul
            const defaultTextField = this.table.fields.find(
                (f) => f.fieldType === "text" || f.fieldType === "long-text"
            );

            return {
                calendarFieldStart: defaultDateField ? defaultDateField._id : null,
                calendarFieldEnd: null,
                cardFields: defaultTextField ? [defaultTextField._id] : [],
                colorByField: null,
            };
        },
        // Arka plan rengine göre metin rengini belirle
        getContrastColor(hexcolor) {
            if (!hexcolor) return "#000000";
            console.log("getContrastColor", hexcolor);
            // RGB'ye dönüştür
            const r = parseInt(hexcolor.slice(1, 3), 16);
            const g = parseInt(hexcolor.slice(3, 5), 16);
            const b = parseInt(hexcolor.slice(5, 7), 16);

            // Parlaklık hesapla (YIQ formülü)
            const yiq = (r * 299 + g * 587 + b * 114) / 1000;

            return yiq >= 128 ? "#000000" : "#FFFFFF";
        },
        switchToTableView() {
            this.$emit("board-view-changed", "table");
        },
    },
};
</script>

<style lang="scss">
.table-calendar-view {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;

    .calendar-header {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 16px;

        .color-legend-wrapper {
            flex: 1;
            overflow: hidden; // Scroll barı gizle

            .color-legend {
                display: flex;
                flex-wrap: nowrap; // Tek satırda tut
                gap: 12px;
                padding: 8px 16px;
                background: var(--color-bg-default);
                border-radius: 4px;
                overflow-x: auto;
                scrollbar-width: none; // Firefox için scroll barı gizle
                -ms-overflow-style: none; // IE için scroll barı gizle

                &::-webkit-scrollbar {
                    display: none; // Chrome için scroll barı gizle
                }

                .legend-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-shrink: 0; // Öğelerin küçülmesini engelle

                    .color-box {
                        width: 16px;
                        height: 16px;
                        border-radius: 4px;
                        border: 1px solid rgba(0, 0, 0, 0.1);
                    }

                    span {
                        font-size: 12px;
                        color: var(--color-text-default);
                        white-space: nowrap; // Metni tek satırda tut
                    }
                }
            }
        }

        .supsis-button {
            flex-shrink: 0; // Butonun küçülmesini engelle
        }
    }

    .fc {
        background: var(--color-bg-default);
        border-radius: 5px;
        border: 1px solid var(--color-border-default);

        // Header stilleri
        .fc-toolbar {
            padding: 16px;
            margin: 0 !important;

            .fc-toolbar-title {
                font-size: 16px;
                font-weight: 600;
                color: var(--color-text-strong-default);
            }

            .fc-button {
                background: var(--color-bg-neutral-default);
                border: 1px solid var(--color-border-default);
                color: var(--color-text-default);
                font-weight: 500;
                padding: 4px 12px;
                height: 32px;

                &:hover {
                    background: var(--color-bg-hover);
                }

                &.fc-button-active {
                    background: var(--color-bg-selected);
                    border-color: var(--color-border-selected);
                }
            }
        }

        // Tablo başlık stilleri
        .fc-col-header {
            .fc-col-header-cell {
                padding: 8px;
                background: var(--color-bg-neutral-low-default);
                border-color: var(--color-border-default);

                .fc-col-header-cell-cushion {
                    color: var(--color-text-strong-default);
                    font-weight: 600;
                    font-size: 14px;
                    text-decoration: none;
                }
            }
        }

        // Takvim hücre stilleri
        .fc-daygrid-day {
            border-color: var(--color-border-default);

            .fc-daygrid-day-top {
                padding: 4px;

                .fc-daygrid-day-number {
                    color: var(--color-text-default);
                    text-decoration: none;
                    font-size: 14px;
                }
            }

            &.fc-day-today {
                background: var(--color-bg-selected-weak);
            }
        }

        // Event stilleri
        .fc-event {
            border-radius: 4px;
            padding: 2px 4px;
            margin: 1px 0;
            border: none; // Border kaldırıldı

            .fc-event-main {
                padding: 2px 4px;
            }

            .fc-event-title {
                font-size: 12px;
                font-weight: 500;
                color: var(--color-text-default);
            }

            &:hover {
                filter: brightness(0.9);
            }
        }

        // Diğer günler (önceki/sonraki ay)
        .fc-day-other {
            background: var(--color-bg-neutral-low-default);
            opacity: 0.7;
        }
    }
    .fc-event-main-frame {
        color: var(--color-text-default);
    }
    .fc-v-event {
        min-height: max-content;
    }

    .calendar-empty-state {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;

        .empty-state-content {
            text-align: center;

            h3 {
                font-size: 1.5rem;
                margin-bottom: 1rem;
                color: var(--color-text-strong-default);
            }

            p {
                color: var(--color-text-default);
                margin-bottom: 2rem;
                max-width: 500px;
                line-height: 1.5;
            }
        }

        :deep(.ant-empty-image) {
            height: 200px;
            margin-bottom: 2rem;
        }
    }
}
</style>
